import { useCallback, useRef, useState } from 'react';

export const DEFAULT_IN_FLIGHT_KEY = 'default';

/**
 * Starts `start()` and returns a promise with the same outcome, calling
 * `onSettle` once it resolves or rejects. A synchronous throw from `start`
 * becomes a rejection. The rejection is passed on, not swallowed, so an
 * error the caller does not handle still surfaces as an unhandled rejection
 * exactly as it would without the guard.
 */
export const guardAsync = (start, onSettle) => {
  let promise;
  try {
    promise = Promise.resolve(start());
  } catch (error) {
    promise = Promise.reject(error);
  }
  return promise.then(
    (value) => {
      onSettle();
      return value;
    },
    (error) => {
      onSettle();
      throw error;
    },
  );
};

/**
 * In-flight guard for actions that trigger slow server-side work.
 *
 * `run(key, action)` starts `action` (which should return a promise) unless an
 * action with the same `key` is still pending. While one is pending the
 * pending promise is returned instead and no second request is issued.
 * The key can be omitted when a component has a single guarded action.
 *
 * `isPending(key)` and `pending` drive `loading` / `disabled` props.
 *
 * The returned promise settles the same way `action` does; handle errors
 * inside `action` (e.g. `.catch(notifyError())`) or on the returned promise
 * as you would without the guard.
 *
 * `Button` already applies this guard to any `onClick` that returns a
 * promise. Use this hook when the pending state has to be shared between
 * several controls, or when the trigger is not a `Button`.
 */
const useInFlight = () => {
  const inFlightRef = useRef(new Map());
  const [pendingKeys, setPendingKeys] = useState(() => new Set());

  const run = useCallback((keyOrAction, maybeAction) => {
    const hasKey = typeof keyOrAction !== 'function';
    const key = hasKey ? keyOrAction : DEFAULT_IN_FLIGHT_KEY;
    const action = hasKey ? maybeAction : keyOrAction;

    const existing = inFlightRef.current.get(key);
    if (existing) {
      return existing;
    }

    let guarded;
    const settle = () => {
      if (inFlightRef.current.get(key) === guarded) {
        inFlightRef.current.delete(key);
      }
      setPendingKeys((prev) => {
        if (!prev.has(key)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    };

    guarded = guardAsync(action, settle);
    inFlightRef.current.set(key, guarded);
    setPendingKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });

    return guarded;
  }, []);

  const isPending = useCallback(
    (key = DEFAULT_IN_FLIGHT_KEY) => pendingKeys.has(key),
    [pendingKeys],
  );

  return { run, isPending, pending: pendingKeys.size > 0 };
};

export default useInFlight;
